/**
 * @typedef {Object} ToneStep
 * @property {number} freq Hz
 * @property {number} [start] offset em segundos
 * @property {number} [duration] segundos
 * @property {number} [gain] 0–1
 * @property {"sine"|"triangle"|"square"} [wave]
 */

/** Chaves alinhadas a NOTIFICATION_TYPES em types.js (sem import — compatível com script Node). */
export const NOTIFICATION_SOUND_TYPE_KEYS = [
  "chat_message",
  "new_request",
  "request_assigned",
  "response_sla",
  "voice_queue",
  "voice_call",
  "team_message",
  "team_mention",
  "agenda_reminder",
  "task_due",
  "task_overdue",
  "task_completed",
  "system",
  "billing",
  "security",
  "legacy_socket",
];

/** @param {number} freq @param {number} [start] @param {number} [duration] @param {number} [gain] @returns {ToneStep[]} */
export function chimeSteps(freq, start = 0, duration = 0.38, gain = 0.05) {
  return [
    { freq, start, duration, gain },
    { freq: freq * 1.498, start, duration: duration * 0.92, gain: gain * 0.42 },
    { freq: freq * 2, start: start + 0.02, duration: duration * 0.55, gain: gain * 0.18 },
  ];
}

/** Perfis usados para gerar WAV e fallback Web Audio. */
export const NOTIFICATION_TONE_PROFILES = {
  chat_message: [{ freq: 740, duration: 0.09, gain: 0.055 }],

  new_request: [
    { freq: 587, duration: 0.1, gain: 0.07 },
    { freq: 740, start: 0.11, duration: 0.12, gain: 0.075 },
    { freq: 880, start: 0.24, duration: 0.14, gain: 0.08 },
  ],

  response_sla: [
    { freq: 587, duration: 0.1, gain: 0.07 },
    { freq: 740, start: 0.11, duration: 0.12, gain: 0.075 },
    { freq: 880, start: 0.24, duration: 0.14, gain: 0.08 },
  ],

  /** Mesmo perfil de urgência da fila de solicitações — ligação na linha. */
  voice_queue: [
    { freq: 587, duration: 0.1, gain: 0.07 },
    { freq: 740, start: 0.11, duration: 0.12, gain: 0.075 },
    { freq: 880, start: 0.24, duration: 0.14, gain: 0.08 },
  ],

  voice_call: [
    { freq: 587, duration: 0.1, gain: 0.07 },
    { freq: 740, start: 0.11, duration: 0.12, gain: 0.075 },
    { freq: 880, start: 0.24, duration: 0.14, gain: 0.08 },
  ],

  request_assigned: [
    { freq: 784, duration: 0.11, gain: 0.065 },
    { freq: 622, start: 0.13, duration: 0.16, gain: 0.072 },
  ],

  team_message: [
    { freq: 494, duration: 0.07, gain: 0.045 },
    { freq: 523, start: 0.1, duration: 0.08, gain: 0.048 },
  ],

  team_mention: [
    { freq: 587, duration: 0.07, gain: 0.06 },
    { freq: 698, start: 0.09, duration: 0.07, gain: 0.062 },
    { freq: 831, start: 0.18, duration: 0.1, gain: 0.068 },
  ],

  agenda_reminder: chimeSteps(523, 0, 0.4, 0.052),

  task_due: [
    { freq: 440, duration: 0.14, gain: 0.058 },
    { freq: 523, start: 0.16, duration: 0.12, gain: 0.05 },
  ],

  task_overdue: [
    { freq: 349, duration: 0.1, gain: 0.072, wave: "triangle" },
    { freq: 349, start: 0.14, duration: 0.1, gain: 0.078, wave: "triangle" },
    { freq: 392, start: 0.3, duration: 0.14, gain: 0.07, wave: "triangle" },
  ],

  task_completed: [
    { freq: 523, duration: 0.1, gain: 0.055 },
    { freq: 659, start: 0.12, duration: 0.12, gain: 0.05 },
  ],

  task_board_invite: [
    { freq: 784, duration: 0.11, gain: 0.065 },
    { freq: 622, start: 0.13, duration: 0.16, gain: 0.072 },
  ],

  system: [{ freq: 554, duration: 0.11, gain: 0.05 }],

  billing: [
    { freq: 988, duration: 0.06, gain: 0.05 },
    { freq: 494, start: 0.08, duration: 0.1, gain: 0.055 },
  ],

  security: [
    { freq: 277, duration: 0.09, gain: 0.06, wave: "triangle" },
    { freq: 277, start: 0.12, duration: 0.09, gain: 0.065, wave: "triangle" },
    { freq: 220, start: 0.26, duration: 0.12, gain: 0.06, wave: "triangle" },
  ],

  legacy_socket: [
    { freq: 880, duration: 0.08, gain: 0.055 },
    { freq: 660, start: 0.1, duration: 0.1, gain: 0.05 },
  ],
};

/** Volume de reprodução (0–1) por tipo — normalizado para percepção similar. */
export const NOTIFICATION_SOUND_VOLUME = {
  chat_message: 0.72,
  new_request: 0.88,
  request_assigned: 0.82,
  response_sla: 0.88,
  voice_queue: 0.86,
  voice_call: 0.9,
  team_message: 0.68,
  team_mention: 0.8,
  agenda_reminder: 0.78,
  task_due: 0.76,
  task_overdue: 0.85,
  task_completed: 0.78,
  task_board_invite: 0.82,
  system: 0.7,
  billing: 0.74,
  security: 0.86,
  legacy_socket: 0.75,
};

/** Tipo representativo de cada grupo nas preferências (preview). */
export const GROUP_PREVIEW_SOUND_TYPE = {
  conv: "chat_message",
  queue: "new_request",
  assign: "request_assigned",
  team: "team_mention",
  schedule: "agenda_reminder",
  sys: "system",
};
